import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './AdminReport.css';
import FantasyAdmin from './FantasyAdmin';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3002';
const GENRES = ['sports', 'games', 'movies', 'girl', 'new', 'other', 'default'];
const ROLES = ['user', 'mod', 'super', 'admin'];
const STALE_DAYS = 30;
const TOP_25_SIZE = 25;

const emptyForm = () => ({
  name: '',
  description: '',
  itemInput: '',
  items: [],
  genre: 'sports',
  showcase: false,
  final: false,
});

const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/New_York',
  }) + ' ET';
};

const formatNumber = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString('en-US');
};

const daysSince = (date) => {
  if (!date) return null;
  return Math.floor((Date.now() - new Date(date).getTime()) / (1000 * 60 * 60 * 24));
};

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AdminReport = () => {
  const [adminUser, setAdminUser] = useState(null);
  const [trueStats, setTrueStats] = useState({ users: null, polls: null, votes: null });
  const [publicStats, setPublicStats] = useState({ users: null, polls: null, votes: null });
  const [topics, setTopics] = useState([]);
  const [showcaseTopics, setShowcaseTopics] = useState([]);
  const [votersById, setVotersById] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [editTopicId, setEditTopicId] = useState(null);
  const [republishConfirmed, setRepublishConfirmed] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteNameConfirm, setDeleteNameConfirm] = useState('');
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [roleDrafts, setRoleDrafts] = useState({});
  const [notificationItems, setNotificationItems] = useState([]);
  const [maintenanceOn, setMaintenanceOn] = useState(false);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError('');

    const [trueStatsRes, publicStatsRes, systemRes, showcaseRes, userRes, usersRes, notificationsRes, settingsRes] = await Promise.allSettled([
      axios.get(`${apiUrl}/api/stats`),
      axios.get(`${apiUrl}/api/topics/stats`),
      axios.get(`${apiUrl}/api/topics/system`),
      axios.get(`${apiUrl}/api/topics/showcase`),
      axios.get(`${apiUrl}/api/auth/verify`, { headers: authHeaders() }),
      axios.get(`${apiUrl}/api/auth/users`, { headers: authHeaders() }),
      axios.get(`${apiUrl}/api/notifications`),
      axios.get(`${apiUrl}/api/settings`),
    ]);

    const failures = [];

    if (trueStatsRes.status === 'fulfilled') {
      const data = trueStatsRes.value.data || {};
      setTrueStats({ users: data.users, polls: data.polls, votes: data.votes });
    } else {
      failures.push('true site counts');
    }

    if (publicStatsRes.status === 'fulfilled') {
      const data = publicStatsRes.value.data || {};
      setPublicStats({
        users: data.userCount,
        polls: data.pollCount,
        votes: data.voteCount,
      });
    } else {
      failures.push('public site counts');
    }

    let loadedTopics = [];
    if (systemRes.status === 'fulfilled') {
      loadedTopics = Array.isArray(systemRes.value.data) ? systemRes.value.data : [];
      setTopics(loadedTopics);
    } else {
      failures.push('official polls');
      setTopics([]);
    }

    if (showcaseRes.status === 'fulfilled') {
      setShowcaseTopics(Array.isArray(showcaseRes.value.data) ? showcaseRes.value.data : []);
    } else {
      failures.push('homepage featured polls');
      setShowcaseTopics([]);
    }

    if (userRes.status === 'fulfilled') {
      setAdminUser(userRes.value.data?.user || null);
    } else {
      failures.push('signed-in admin profile');
    }

    if (usersRes.status === 'fulfilled') {
      setUsers(Array.isArray(usersRes.value.data) ? usersRes.value.data : []);
      setRoleDrafts({});
    } else {
      failures.push('user directory');
      setUsers([]);
    }

    if (notificationsRes.status === 'fulfilled') {
      const items = Array.isArray(notificationsRes.value.data?.items)
        ? notificationsRes.value.data.items
        : [];
      setNotificationItems(items.map((item) => ({ text: item.text || '', url: item.url || '' })));
    } else {
      failures.push('notifications');
    }

    if (settingsRes.status === 'fulfilled') {
      setMaintenanceOn(Boolean(settingsRes.value.data?.maintenance));
    } else {
      failures.push('site settings');
    }

    if (loadedTopics.length) {
      const voterEntries = await Promise.all(
        loadedTopics.map((topic) =>
          axios
            .get(`${apiUrl}/api/topics/${topic._id}/voters`)
            .then((res) => [topic._id, res.data?.totalVoters ?? 0])
            .catch(() => [topic._id, null])
        )
      );
      setVotersById(Object.fromEntries(voterEntries));
    } else {
      setVotersById({});
    }

    if (failures.length) {
      setError(`Some dashboard data failed to load: ${failures.join(', ')}.`);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const showcaseIds = useMemo(
    () => new Set(showcaseTopics.map((topic) => topic._id)),
    [showcaseTopics]
  );

  const selectedTopic = useMemo(
    () => topics.find((topic) => topic._id === selectedId) || null,
    [topics, selectedId]
  );

  const alerts = useMemo(() => {
    const items = [];
    topics.forEach((topic) => {
      const voters = votersById[topic._id];
      const ageDays = daysSince(topic.publishDate);
      const featuredOnHome = showcaseIds.has(topic._id);

      if (voters !== null && voters !== undefined && voters <= 1 && !topic.final) {
        items.push({
          id: `${topic._id}-no-votes`,
          level: 'warning',
          text: `"${topic.name}" has no user votes yet.`,
        });
      }

      if (topic.showcase && topic.final) {
        items.push({
          id: `${topic._id}-featured-final`,
          level: 'critical',
          text: `"${topic.name}" is marked featured and final. Home hides closed featured polls.`,
        });
      }

      if (!topic.genre || topic.genre === 'default') {
        items.push({
          id: `${topic._id}-genre`,
          level: 'warning',
          text: `"${topic.name}" has no genre set, so homepage cards use the default background.`,
        });
      }

      if (ageDays !== null && ageDays >= STALE_DAYS && !topic.final) {
        items.push({
          id: `${topic._id}-stale`,
          level: 'warning',
          text: `"${topic.name}" has been open for ${ageDays} days.`,
        });
      }

      if ((topic.items || []).length !== TOP_25_SIZE) {
        items.push({
          id: `${topic._id}-items`,
          level: 'info',
          text: `"${topic.name}" has ${(topic.items || []).length} items, not ${TOP_25_SIZE}.`,
        });
      }

      if (!featuredOnHome && !topic.final) {
        items.push({
          id: `${topic._id}-not-featured`,
          level: 'info',
          text: `"${topic.name}" is open and not currently featured on Home.`,
        });
      }
    });

    const levelOrder = { critical: 0, warning: 1, info: 2 };
    return items.sort((a, b) => levelOrder[a.level] - levelOrder[b.level]);
  }, [topics, votersById, showcaseIds]);

  const visibleAlerts = useMemo(
    () => alerts.filter((alert) => alert.level !== 'info').concat(
      alerts.filter((alert) => alert.level === 'info').slice(0, 4)
    ),
    [alerts]
  );

  const filteredTopics = useMemo(() => {
    const query = search.trim().toLowerCase();
    const list = query
      ? topics.filter((topic) => {
          const haystack = `${topic.name} ${topic.description || ''} ${topic.genre || ''}`.toLowerCase();
          return haystack.includes(query);
        })
      : [...topics];

    return list.sort((a, b) => {
      const aDate = new Date(a.publishDate || a.createdAt || 0).getTime();
      const bDate = new Date(b.publishDate || b.createdAt || 0).getTime();
      return bDate - aDate;
    });
  }, [topics, search]);

  const resetForm = () => {
    setForm(emptyForm());
    setEditTopicId(null);
    setRepublishConfirmed(false);
  };

  const handleAddItems = () => {
    const newItems = form.itemInput
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
      .map((name) => ({ name }));
    if (!newItems.length) return;
    setForm((current) => ({
      ...current,
      items: [...current.items, ...newItems],
      itemInput: '',
    }));
  };

  const handleRemoveItem = (index) => {
    setForm((current) => ({
      ...current,
      items: current.items.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const handleMoveItem = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= form.items.length) return;
    setForm((current) => {
      const next = [...current.items];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return { ...current, items: next };
    });
  };

  const startEdit = (topic) => {
    setEditTopicId(topic._id);
    setSelectedId(topic._id);
    setRepublishConfirmed(false);
    setPendingDelete(null);
    setForm({
      name: topic.name || '',
      description: topic.description || '',
      itemInput: '',
      items: (topic.items || []).map((item) => ({ name: item.name })),
      genre: topic.genre || 'default',
      showcase: Boolean(topic.showcase),
      final: Boolean(topic.final),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCreateOrUpdate = async () => {
    if (!form.name.trim()) {
      setMessage('Poll name is required.');
      return;
    }
    if (!form.items.length) {
      setMessage('Add at least one item before saving.');
      return;
    }
    if (editTopicId && !republishConfirmed) {
      setMessage('Confirm that you understand republishing will reset rankings.');
      return;
    }

    setSaving(true);
    setMessage('');
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        items: form.items.map((item, index) => ({ name: item.name, position: index + 1 })),
        genre: form.genre,
        showcase: form.showcase,
        final: form.final,
      };
      const url = editTopicId
        ? `${apiUrl}/api/topics/system/${editTopicId}`
        : `${apiUrl}/api/topics/system`;
      const method = editTopicId ? 'put' : 'post';
      await axios[method](url, payload, { headers: authHeaders() });
      setMessage(editTopicId
        ? 'Official poll republished. Existing votes are now outdated and users must vote again.'
        : 'Official poll created.');
      resetForm();
      await loadDashboard();
    } catch (err) {
      setMessage('Failed to save official poll: ' + (err.response?.data?.error || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    if (deleteNameConfirm.trim() !== pendingDelete.name) {
      setMessage('Type the exact poll name to confirm delete.');
      return;
    }

    setSaving(true);
    setMessage('');
    try {
      await axios.delete(`${apiUrl}/api/topics/system/${pendingDelete._id}`, {
        headers: authHeaders(),
      });
      setMessage(`Deleted official poll "${pendingDelete.name}". Rankings and vote records were removed.`);
      if (selectedId === pendingDelete._id) setSelectedId(null);
      if (editTopicId === pendingDelete._id) resetForm();
      setPendingDelete(null);
      setDeleteNameConfirm('');
      await loadDashboard();
    } catch (err) {
      setMessage('Failed to delete official poll: ' + (err.response?.data?.error || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const refreshShowcase = async () => {
    try {
      const res = await axios.get(`${apiUrl}/api/topics/showcase`);
      setShowcaseTopics(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError('Failed to refresh homepage featured polls.');
    }
  };

  const handleUpdateFlags = async (topic, flags) => {
    setSaving(true);
    setMessage('');
    try {
      const res = await axios.put(
        `${apiUrl}/api/topics/system/${topic._id}`,
        flags,
        { headers: authHeaders() }
      );
      const updated = res.data || {};
      setTopics((current) => current.map((item) => (
        item._id === topic._id ? { ...item, ...updated } : item
      )));
      await refreshShowcase();

      if (Object.prototype.hasOwnProperty.call(flags, 'showcase')) {
        if (flags.showcase && (updated.final || topic.final)) {
          setMessage(`"${topic.name}" is marked featured, but Home hides closed polls until you reopen it.`);
        } else {
          setMessage(flags.showcase
            ? `"${topic.name}" is now featured on Home.`
            : `"${topic.name}" was removed from Home.`);
        }
      } else if (Object.prototype.hasOwnProperty.call(flags, 'final')) {
        setMessage(flags.final
          ? `"${topic.name}" is closed. Voting is off.`
          : `"${topic.name}" is open for voting again.`);
      }
    } catch (err) {
      setMessage('Failed to update poll: ' + (err.response?.data?.error || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (user) => {
    const nextRole = roleDrafts[user.id] || user.role;
    if (nextRole === user.role) {
      setMessage(`${user.name} is already ${user.role}.`);
      return;
    }
    if (adminUser && user.id === adminUser.id) {
      setMessage('You cannot change your own role.');
      return;
    }
    const confirmed = window.confirm(`Change ${user.name} from ${user.role} to ${nextRole}?`);
    if (!confirmed) return;

    setSaving(true);
    setMessage('');
    try {
      const res = await axios.put(
        `${apiUrl}/api/auth/users/${user.id}/role`,
        { role: nextRole },
        { headers: authHeaders() }
      );
      const updated = res.data?.user || { ...user, role: nextRole };
      setUsers((current) => current.map((item) => (
        item.id === user.id ? { ...item, role: updated.role } : item
      )));
      setRoleDrafts((current) => {
        const next = { ...current };
        delete next[user.id];
        return next;
      });
      setMessage(`${user.name} is now ${updated.role}.`);
    } catch (err) {
      setMessage('Failed to update role: ' + (err.response?.data?.error || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleNotificationChange = (index, field, value) => {
    setNotificationItems((current) => current.map((item, itemIndex) => (
      itemIndex === index ? { ...item, [field]: value } : item
    )));
  };

  const handleAddNotification = () => {
    if (notificationItems.length >= 10) {
      setMessage('A maximum of 10 notifications is allowed.');
      return;
    }
    setNotificationItems((current) => [...current, { text: '', url: '' }]);
  };

  const handleRemoveNotification = (index) => {
    setNotificationItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleMoveNotification = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= notificationItems.length) return;
    setNotificationItems((current) => {
      const next = [...current];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const handleSaveNotifications = async () => {
    const items = notificationItems
      .map((item) => ({ text: item.text.trim(), url: item.url.trim() }))
      .filter((item) => item.text);
    setSaving(true);
    setMessage('');
    try {
      const res = await axios.put(
        `${apiUrl}/api/notifications`,
        { items },
        { headers: authHeaders() }
      );
      const saved = Array.isArray(res.data?.items) ? res.data.items : items;
      setNotificationItems(saved.map((item) => ({ text: item.text || '', url: item.url || '' })));
      window.dispatchEvent(new Event('notifications-updated'));
      setMessage('Left-nav notifications updated. All visitors will see the new list.');
    } catch (err) {
      setMessage('Failed to save notifications: ' + (err.response?.data?.error || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleMaintenance = async () => {
    const next = !maintenanceOn;
    const confirmText = next
      ? 'Put the public site in maintenance? Visitors will see a “be right back” page. You can still use Admin Report to bring it back.'
      : 'Bring the public site back online?';
    if (!window.confirm(confirmText)) return;

    setSaving(true);
    setMessage('');
    try {
      const res = await axios.put(
        `${apiUrl}/api/settings`,
        { maintenance: next },
        { headers: authHeaders() }
      );
      const enabled = Boolean(res.data?.maintenance);
      setMaintenanceOn(enabled);
      window.dispatchEvent(new CustomEvent('maintenance-updated', { detail: { maintenance: enabled } }));
      setMessage(enabled
        ? 'Maintenance is on. Regular visitors see the locker-room page. Admins can still work here.'
        : 'Maintenance is off. The site is open again.');
    } catch (err) {
      setMessage('Failed to update maintenance: ' + (err.response?.data?.error || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const query = userSearch.trim().toLowerCase();
    const list = query
      ? users.filter((user) => {
          const haystack = `${user.name} ${user.email} ${user.role}`.toLowerCase();
          return haystack.includes(query);
        })
      : [...users];

    const rank = { admin: 0, super: 1, mod: 2, user: 3 };
    return list.sort((a, b) => {
      const roleDiff = (rank[a.role] ?? 9) - (rank[b.role] ?? 9);
      if (roleDiff !== 0) return roleDiff;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [users, userSearch]);

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div>
          <h1>Admin Dashboard</h1>
          <p className="admin-subtitle">
            {adminUser
              ? `Signed in as ${adminUser.name} (${adminUser.role || 'admin'})`
              : 'Official poll operations'}
          </p>
        </div>
        <button type="button" onClick={loadDashboard} disabled={loading || saving}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </header>

      {error && <p className="admin-banner admin-banner-error">{error}</p>}
      {message && <p className="admin-banner admin-banner-info">{message}</p>}

      <section className={`admin-card ${maintenanceOn ? 'admin-maintenance-on' : ''}`}>
        <h2>Maintenance</h2>
        <p className="admin-footnote">
          {maintenanceOn
            ? 'The public site is in the locker room. Signed-in admins can still use the app and turn this off.'
            : 'The site is live. Flip this only for a rollback, a deploy scare, or planned work.'}
        </p>
        <button
          type="button"
          className={maintenanceOn ? 'admin-danger' : ''}
          onClick={handleToggleMaintenance}
          disabled={saving}
        >
          {maintenanceOn ? 'Bring site back online' : 'Put site in maintenance'}
        </button>
      </section>

      <FantasyAdmin onMessage={setMessage} />

      <section className="admin-card">
        <h2>Ops checklist</h2>
        <ul className="admin-checklist">
          <li>
            Left-nav notifications are edited in the section below. Save there instead of changing App.js.
          </li>
          <li>
            Feature on Home and Close poll do not reset rankings. Edit / Republish does.
          </li>
          <li>
            Maintenance mode is toggled in the card below. Admins can still open this dashboard while it is on.
          </li>
          <li>
            Role changes apply on the user's next page load. You cannot change your own role.
          </li>
          <li className="admin-checklist-links">
            Shortcuts:
            {' '}
            <Link to="/">Home</Link>
            {' · '}
            <Link to="/system-topics">Official Polls</Link>
            {' · '}
            <Link to="/college-fantasy">College Fantasy</Link>
          </li>
        </ul>
      </section>

      <section className="admin-card">
        <h2>Left-nav notifications</h2>
        <p className="admin-footnote">
          These items appear in the Notifications box on every page. Use a site path like
          {' '}<code>/system-topics</code> or <code>/topic/ID</code> for internal links, or a full URL for X and other sites.
          Blank lines are dropped when you save. Maximum 10 items.
        </p>
        {notificationItems.length ? (
          <ol className="admin-notification-list">
            {notificationItems.map((item, index) => (
              <li key={index} className="admin-notification-row">
                <label className="admin-label" htmlFor={`admin-notification-text-${index}`}>Text</label>
                <textarea
                  id={`admin-notification-text-${index}`}
                  value={item.text}
                  onChange={(e) => handleNotificationChange(index, 'text', e.target.value)}
                  placeholder="Notification text"
                  maxLength="500"
                />
                <label className="admin-label" htmlFor={`admin-notification-url-${index}`}>Optional link</label>
                <input
                  id={`admin-notification-url-${index}`}
                  value={item.url}
                  onChange={(e) => handleNotificationChange(index, 'url', e.target.value)}
                  placeholder="https://x.com/TheMyTop25 or /system-topics"
                  maxLength="1000"
                />
                <div className="admin-item-actions">
                  <button type="button" onClick={() => handleMoveNotification(index, index - 1)} disabled={index === 0}>Up</button>
                  <button type="button" onClick={() => handleMoveNotification(index, index + 1)} disabled={index === notificationItems.length - 1}>Down</button>
                  <button type="button" onClick={() => handleRemoveNotification(index)}>Remove</button>
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <p>No notifications yet. Add one to show a message in the left nav.</p>
        )}
        <div className="admin-form-actions">
          <button type="button" onClick={handleAddNotification} disabled={saving || notificationItems.length >= 10}>
            Add notification
          </button>
          <button type="button" onClick={handleSaveNotifications} disabled={saving}>
            Save notifications
          </button>
        </div>
      </section>

      <section className="admin-stats">
        <article className="admin-stat-card">
          <h3>Users</h3>
          <p className="admin-stat-true">{formatNumber(trueStats.users)}</p>
          <p className="admin-stat-public">Public banner: {formatNumber(publicStats.users)}</p>
        </article>
        <article className="admin-stat-card">
          <h3>Polls</h3>
          <p className="admin-stat-true">{formatNumber(trueStats.polls)}</p>
          <p className="admin-stat-public">Public banner: {formatNumber(publicStats.polls)}</p>
        </article>
        <article className="admin-stat-card">
          <h3>Votes</h3>
          <p className="admin-stat-true">{formatNumber(trueStats.votes)}</p>
          <p className="admin-stat-public">Public banner: {formatNumber(publicStats.votes)}</p>
        </article>
      </section>
      <p className="admin-footnote">
        True counts come from `/api/stats`. The public Home banner uses padded `/api/topics/stats` numbers.
      </p>

      <section className="admin-card">
        <h2>Alerts</h2>
        {loading && !topics.length ? (
          <p>Loading alerts…</p>
        ) : visibleAlerts.length ? (
          <ul className="admin-alerts">
            {visibleAlerts.map((alert) => (
              <li key={alert.id} className={`admin-alert admin-alert-${alert.level}`}>
                <strong>{alert.level === 'critical' ? 'Action needed' : alert.level === 'warning' ? 'Watch' : 'Note'}:</strong>
                {' '}
                {alert.text}
              </li>
            ))}
          </ul>
        ) : (
          <p>No poll issues detected.</p>
        )}
        {alerts.length > visibleAlerts.length && (
          <p className="admin-footnote">
            Showing the highest-priority alerts. Open and not featured is listed on each poll row.
          </p>
        )}
      </section>

      <div className="admin-split">
        <section className="admin-card">
          <h2>Featured on Home</h2>
          {showcaseTopics.length ? (
            <ul className="admin-featured-list">
              {showcaseTopics.map((topic) => (
                <li key={topic._id}>
                  <Link to={`/topic/${topic._id}`}>{topic.name}</Link>
                  <span className="admin-badge admin-badge-genre">{topic.genre || 'default'}</span>
                  {topic.final && <span className="admin-badge admin-badge-closed">Closed</span>}
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => handleUpdateFlags(topic, { showcase: false })}
                  >
                    Remove from Home
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p>No polls are currently featured on Home.</p>
          )}
        </section>

        <section className="admin-card">
          <h2>{editTopicId ? 'Republish official poll' : 'Create official poll'}</h2>
          {editTopicId && (
            <p className="admin-banner admin-banner-warning">
              Updating an official poll resets ranking points, sets a new publish date, and marks existing user votes as outdated.
            </p>
          )}
          <label className="admin-label" htmlFor="admin-poll-name">Poll name</label>
          <input
            id="admin-poll-name"
            value={form.name}
            onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))}
            placeholder="Topic Name"
            maxLength="500"
          />
          <label className="admin-label" htmlFor="admin-poll-description">Description</label>
          <textarea
            id="admin-poll-description"
            value={form.description}
            onChange={(e) => setForm((current) => ({ ...current, description: e.target.value }))}
            placeholder="Description"
            maxLength="500"
          />
          <label className="admin-label" htmlFor="admin-poll-genre">Genre</label>
          <select
            id="admin-poll-genre"
            className="admin-select"
            value={form.genre}
            onChange={(e) => setForm((current) => ({ ...current, genre: e.target.value }))}
          >
            {GENRES.map((genre) => (
              <option key={genre} value={genre}>{genre}</option>
            ))}
          </select>
          <label className="admin-label" htmlFor="admin-poll-items">Add items (comma-separated)</label>
          <textarea
            id="admin-poll-items"
            value={form.itemInput}
            onChange={(e) => setForm((current) => ({ ...current, itemInput: e.target.value }))}
            placeholder="Add item, comma separated"
            maxLength="10000"
          />
          <button type="button" onClick={handleAddItems}>Add Item</button>
          <h3>Items in order ({form.items.length})</h3>
          <p className="admin-footnote">Initial points follow this order. Lower in the list means more points and a worse rank.</p>
          <label className="admin-confirm">
            <input
              type="checkbox"
              checked={form.showcase}
              onChange={(e) => setForm((current) => ({ ...current, showcase: e.target.checked }))}
            />
            Feature on Home
          </label>
          <label className="admin-confirm">
            <input
              type="checkbox"
              checked={form.final}
              onChange={(e) => setForm((current) => ({ ...current, final: e.target.checked }))}
            />
            Close poll (final)
          </label>
          {form.items.length ? (
            <ol className="admin-item-list">
              {form.items.map((item, index) => (
                <li key={`${item.name}-${index}`}>
                  <span>{item.name}</span>
                  <span className="admin-item-actions">
                    <button type="button" onClick={() => handleMoveItem(index, index - 1)} disabled={index === 0}>Up</button>
                    <button type="button" onClick={() => handleMoveItem(index, index + 1)} disabled={index === form.items.length - 1}>Down</button>
                    <button type="button" onClick={() => handleRemoveItem(index)}>Remove</button>
                  </span>
                </li>
              ))}
            </ol>
          ) : (
            <p>No items added yet.</p>
          )}
          {editTopicId && (
            <label className="admin-confirm">
              <input
                type="checkbox"
                checked={republishConfirmed}
                onChange={(e) => setRepublishConfirmed(e.target.checked)}
              />
              I understand this will reset rankings and require voters to vote again.
            </label>
          )}
          <div className="admin-form-actions">
            <button type="button" onClick={handleCreateOrUpdate} disabled={saving}>
              {editTopicId ? 'Republish Official Poll' : 'Create Official Poll'}
            </button>
            {(editTopicId || form.name || form.items.length) && (
              <button type="button" onClick={resetForm} disabled={saving}>Cancel</button>
            )}
          </div>
        </section>
      </div>

      <section className="admin-card">
        <div className="admin-table-header">
          <h2>Official polls</h2>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by name, description, or genre"
            aria-label="Filter official polls"
          />
        </div>
        <p className="admin-footnote">
          Voter counts include the official seed (+1). A count of 1 usually means nobody has voted yet.
        </p>
        <div className="admin-table-wrap">
          {loading && !topics.length ? (
            <p>Loading official polls…</p>
          ) : filteredTopics.length ? (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Genre</th>
                  <th>Items</th>
                  <th>Voters</th>
                  <th>Status</th>
                  <th>Published</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTopics.map((topic) => {
                  const featuredOnHome = showcaseIds.has(topic._id);
                  const voters = votersById[topic._id];
                  return (
                    <tr
                      key={topic._id}
                      className={selectedId === topic._id ? 'admin-row-selected' : ''}
                    >
                      <td>
                        <Link to={`/topic/${topic._id}`}>{topic.name}</Link>
                        {topic.description && (
                          <div className="admin-muted">{topic.description}</div>
                        )}
                      </td>
                      <td>
                        <span className="admin-badge admin-badge-genre">{topic.genre || 'default'}</span>
                      </td>
                      <td>{(topic.items || []).length}</td>
                      <td>{voters === null || voters === undefined ? '—' : formatNumber(voters)}</td>
                      <td>
                        {topic.final
                          ? <span className="admin-badge admin-badge-closed">Closed</span>
                          : <span className="admin-badge admin-badge-open">Open</span>}
                        {featuredOnHome
                          ? <span className="admin-badge admin-badge-featured">On Home</span>
                          : <span className="admin-badge admin-badge-muted">Not featured</span>}
                        {topic.showcase && !featuredOnHome && (
                          <span className="admin-badge admin-badge-warning">Featured but closed</span>
                        )}
                      </td>
                      <td>{formatDate(topic.publishDate)}</td>
                      <td className="admin-row-actions">
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => handleUpdateFlags(topic, { showcase: !topic.showcase })}
                        >
                          {topic.showcase ? 'Remove from Home' : 'Feature on Home'}
                        </button>
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => handleUpdateFlags(topic, { final: !topic.final })}
                        >
                          {topic.final ? 'Reopen poll' : 'Close poll'}
                        </button>
                        <button type="button" onClick={() => setSelectedId(topic._id)}>Inspect</button>
                        <button type="button" onClick={() => startEdit(topic)}>Edit / Republish</button>
                        <button
                          type="button"
                          className="admin-danger"
                          onClick={() => {
                            setPendingDelete(topic);
                            setDeleteNameConfirm('');
                            setSelectedId(topic._id);
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p>No official polls match this view.</p>
          )}
        </div>
      </section>

      {pendingDelete && (
        <section className="admin-card admin-delete-panel">
          <h2>Delete official poll</h2>
          <p>
            Delete <strong>{pendingDelete.name}</strong>? This permanently removes the official poll,
            all user rankings, and vote records.
          </p>
          <p>
            Voters recorded: <strong>{formatNumber(votersById[pendingDelete._id])}</strong>
          </p>
          <label className="admin-label" htmlFor="admin-delete-confirm">
            Type the poll name to confirm
          </label>
          <input
            id="admin-delete-confirm"
            value={deleteNameConfirm}
            onChange={(e) => setDeleteNameConfirm(e.target.value)}
            placeholder={pendingDelete.name}
          />
          <div className="admin-form-actions">
            <button type="button" className="admin-danger" onClick={handleDelete} disabled={saving}>
              Delete poll
            </button>
            <button
              type="button"
              onClick={() => {
                setPendingDelete(null);
                setDeleteNameConfirm('');
              }}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </section>
      )}

      {selectedTopic && (
        <section className="admin-card">
          <h2>Poll snapshot</h2>
          <p>
            <Link to={`/topic/${selectedTopic._id}`}>{selectedTopic.name}</Link>
            {' · '}
            {selectedTopic.genre || 'default'}
            {' · '}
            {(selectedTopic.items || []).length} items
            {' · '}
            {formatNumber(votersById[selectedTopic._id])} voters
          </p>
          <p className="admin-muted">{selectedTopic.description || 'No description'}</p>
          <p className="admin-muted">Published {formatDate(selectedTopic.publishDate)}</p>
          <div className="admin-form-actions">
            <button
              type="button"
              disabled={saving}
              onClick={() => handleUpdateFlags(selectedTopic, { showcase: !selectedTopic.showcase })}
            >
              {selectedTopic.showcase ? 'Remove from Home' : 'Feature on Home'}
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => handleUpdateFlags(selectedTopic, { final: !selectedTopic.final })}
            >
              {selectedTopic.final ? 'Reopen poll' : 'Close poll'}
            </button>
          </div>
          <table className="admin-snapshot-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Name</th>
                <th>Points</th>
              </tr>
            </thead>
            <tbody>
              {(selectedTopic.items || []).slice(0, TOP_25_SIZE).map((item, index) => (
                <tr key={`${item.name}-${index}`}>
                  <td>{index + 1}.</td>
                  <td>{item.name}</td>
                  <td>{item.points || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(selectedTopic.items || []).length > TOP_25_SIZE && (
            <p className="admin-footnote">
              Receiving votes: {(selectedTopic.items || []).slice(TOP_25_SIZE, TOP_25_SIZE + 3).map((item) => item.name).join(', ')}
            </p>
          )}
        </section>
      )}

      <section className="admin-card">
        <div className="admin-table-header">
          <h2>Users and roles</h2>
          <input
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            placeholder="Filter by name, email, or role"
            aria-label="Filter users"
          />
        </div>
        <p className="admin-footnote">
          Promote or demote users between user, mod, super, and admin. Admins can open this dashboard.
          You cannot change your own role, and the last admin cannot be demoted.
        </p>
        <div className="admin-table-wrap">
          {loading && !users.length ? (
            <p>Loading users…</p>
          ) : filteredUsers.length ? (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => {
                  const isSelf = adminUser && user.id === adminUser.id;
                  const draftRole = roleDrafts[user.id] || user.role;
                  return (
                    <tr key={user.id}>
                      <td>
                        {user.name}
                        {isSelf && <span className="admin-badge admin-badge-muted">You</span>}
                      </td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`admin-badge admin-badge-role admin-badge-role-${user.role}`}>
                          {user.role}
                        </span>
                      </td>
                      <td>{formatDate(user.createdAt)}</td>
                      <td className="admin-row-actions">
                        <select
                          className="admin-role-select"
                          aria-label={`Role for ${user.name}`}
                          value={draftRole}
                          disabled={saving || isSelf}
                          onChange={(e) => setRoleDrafts((current) => ({
                            ...current,
                            [user.id]: e.target.value,
                          }))}
                        >
                          {ROLES.map((role) => (
                            <option key={role} value={role}>{role}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          disabled={saving || isSelf || draftRole === user.role}
                          onClick={() => handleRoleChange(user)}
                        >
                          Apply role
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p>No users match this view.</p>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminReport;
